/**
 * FinalChecksSettingsModal Component
 *
 * Modal for editing final check items configuration from the settings dashboard.
 * This is the SETTINGS modal (saves directly to global settings), NOT the workflow modal
 * (which handles session drafts and "apply to future" logic).
 *
 * Key behaviors:
 * - Manual Save/Discard pattern
 * - Dirty-close blocking: when dirty, backdrop/X/Esc are blocked
 * - SortableList for drag-and-drop reordering
 * - Inline editing (no nested modals)
 * - Immediate delete (discard to undo)
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Card,
  CardBody,
  SortableList,
  DragHandle,
  EmptyState,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import type { PreTradingSettings } from '@/types/schema/PreTradingSettings';

// ============================================================================
// Types
// ============================================================================

/**
 * Final check item type derived from schema
 */
export type FinalCheckItem = PreTradingSettings['finalCheckItems'][number];

export interface FinalChecksSettingsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Async callback when items are saved - should persist to settings */
  onSave: (updatedItems: FinalCheckItem[]) => Promise<void>;
  /** Initial items from current settings */
  initialItems: FinalCheckItem[];
}

// ============================================================================
// Component
// ============================================================================

export const FinalChecksSettingsModal: React.FC<FinalChecksSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialItems,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localItems, setLocalItems] = useState<FinalCheckItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Per-item validation errors (keyed by item id)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Baseline for dirty tracking (stores JSON string)
  const baselineKeyRef = useRef<string>('');

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Track newly added item ID for auto-focus
  const newlyAddedIdRef = useRef<string | null>(null);

  // Clear auto-focus ref after render (avoids side effects during render)
  useEffect(() => {
    if (newlyAddedIdRef.current === null) return;

    // Clear after a microtask to ensure the Input has received autoFocus
    const timeoutId = setTimeout(() => {
      newlyAddedIdRef.current = null;
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [localItems]);

  // Compute dirty state using stable JSON comparison
  const localItemsKey = useMemo(() => JSON.stringify(localItems), [localItems]);
  const isDirty = localItemsKey !== baselineKeyRef.current;

  // Check if there are any validation errors
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  // Initialize local state from initialItems when modal opens
  // Changes to initialItems while modal is open are ignored (only re-sync on open transition)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Deep copy items
      const itemsCopy: FinalCheckItem[] = initialItems.map((item) => ({ ...item }));

      // Set local items and baseline
      setLocalItems(itemsCopy);
      baselineKeyRef.current = JSON.stringify(itemsCopy);

      // Reset ALL state to prevent stale UI
      setValidationErrors({});
      setIsSaving(false);
      newlyAddedIdRef.current = null;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialItems]);

  // ============================================================================
  // Inline Item Handlers
  // ============================================================================

  /**
   * Handle inline label change with live validation
   */
  const handleItemChange = useCallback((id: string, label: string) => {
    // Update the item label
    setLocalItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, label } : item))
    );

    // Clear validation error when user starts typing (will re-validate on blur/save)
    setValidationErrors((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return prev;
    });
  }, []);

  /**
   * Handle blur - trim label and validate synchronously
   */
  const handleItemBlur = useCallback((id: string, currentLabel: string) => {
    const trimmedLabel = currentLabel.trim();

    // Update item with trimmed label
    setLocalItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, label: trimmedLabel } : item))
    );

    // Validate synchronously - set or clear error based on trimmed value
    if (!trimmedLabel) {
      setValidationErrors((prev) => ({ ...prev, [id]: 'Label is required' }));
    } else {
      // Clear error if label is now valid
      setValidationErrors((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, []);

  /**
   * Add new empty item with auto-focus
   */
  const handleAddItem = useCallback(() => {
    const newId = generateUUID();
    const newItem: FinalCheckItem = {
      id: newId,
      label: '',
    };

    setLocalItems((prev) => [...prev, newItem]);

    // Track for auto-focus
    newlyAddedIdRef.current = newId;

    // Add validation error for empty label (will be cleared when user types)
    setValidationErrors((prev) => ({ ...prev, [newId]: 'Label is required' }));
  }, []);

  /**
   * Delete item immediately (no confirmation)
   */
  const handleDeleteItem = useCallback((id: string) => {
    setLocalItems((prev) => prev.filter((item) => item.id !== id));

    // Clear any validation error for deleted item
    setValidationErrors((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return prev;
    });
  }, []);

  /**
   * Handle reorder from SortableList
   */
  const handleReorder = useCallback((reorderedItems: FinalCheckItem[]) => {
    setLocalItems(reorderedItems);
  }, []);

  // ============================================================================
  // Save/Discard Handlers
  // ============================================================================

  /**
   * Validate all items and save if valid
   */
  const handleSave = useCallback(async () => {
    // Validate all items
    const errors: Record<string, string> = {};
    const trimmedItems: FinalCheckItem[] = localItems.map((item) => {
      const trimmedLabel = item.label.trim();
      if (!trimmedLabel) {
        errors[item.id] = 'Label is required';
      }
      return { ...item, label: trimmedLabel };
    });

    // If any errors, update state and abort save
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setLocalItems(trimmedItems);
      return;
    }

    setIsSaving(true);

    try {
      await onSave(trimmedItems);

      // On successful save: update baseline (clears dirty state), then close
      baselineKeyRef.current = JSON.stringify(trimmedItems);
      onClose();
    } catch (err) {
      // On save failure: keep modal open, show error toast, do NOT reset baseline
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [localItems, onSave, onClose, showToast]);

  /**
   * Discard changes and close
   */
  const handleDiscard = useCallback(() => {
    // Reset localItems to baseline (clears dirty), then close modal
    const baseline = JSON.parse(baselineKeyRef.current) as FinalCheckItem[];
    setLocalItems(baseline);
    setValidationErrors({});
    onClose();
  }, [onClose]);

  // ============================================================================
  // Item Row Renderer
  // ============================================================================

  const renderItemRow = useCallback(
    (item: FinalCheckItem, dragHandleProps: DragHandleProps) => {
      const error = validationErrors[item.id];
      const shouldAutoFocus = newlyAddedIdRef.current === item.id;

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
        >
          <CardBody className="p-3">
            <div className="flex items-center gap-3">
              <DragHandle dragHandleProps={dragHandleProps} />

              {/* Inline Input */}
              <div className="flex-1 min-w-0">
                <Input
                  size="sm"
                  placeholder="e.g., Review key levels"
                  value={item.label}
                  onChange={(e) => handleItemChange(item.id, e.target.value)}
                  onBlur={(e) => handleItemBlur(item.id, e.target.value)}
                  isInvalid={!!error}
                  errorMessage={error}
                  autoFocus={shouldAutoFocus}
                  classNames={{ input: 'text-base' }}
                />
              </div>

              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => handleDeleteItem(item.id)}
                aria-label={`Delete ${item.label || 'item'}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    },
    [validationErrors, handleItemChange, handleItemBlur, handleDeleteItem]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      scrollBehavior="inside"
      size="2xl"
      isDismissable={!isDirty}
      hideCloseButton={isDirty}
      isKeyboardDismissDisabled={isDirty}
    >
      <ModalContent>
        <ModalHeader>
          <span className="text-lg font-semibold">Final Checks</span>
        </ModalHeader>
        <ModalBody className="gap-4">
          {/* Items List */}
          {localItems.length > 0 ? (
            <SortableList
              items={localItems}
              onReorder={handleReorder}
              renderItem={renderItemRow}
              className="space-y-2"
            />
          ) : (
            <EmptyState
              title="No check items configured"
              description="Add items to create your pre-trading final checklist."
            />
          )}

          {/* Add Item Button */}
          <Button
            variant="flat"
            color="primary"
            startContent={<Plus className="w-4 h-4" />}
            onPress={handleAddItem}
            className="w-full md:w-auto"
          >
            Add Check Item
          </Button>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="light"
            onPress={isDirty ? handleDiscard : onClose}
            isDisabled={isSaving}
          >
            {isDirty ? 'Discard' : 'Cancel'}
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!isDirty || hasValidationErrors || isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
